import type { AuthResponse } from '@/types/api'

const ACCESS_TOKEN_KEY = 'doers.accessToken'
const REFRESH_TOKEN_KEY = 'doers.refreshToken'

type Listener = () => void

const listeners = new Set<Listener>()

/**
 * Tokens live in localStorage so a page refresh doesn't log the user out —
 * the backend issues refresh tokens in the response body (not an httpOnly
 * cookie), so this is the only persistence option without backend changes.
 * Traded off against XSS exposure; worth revisiting if/when the backend
 * moves to a cookie-based refresh flow.
 */
export const tokenStore = {
  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY)
  },
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  },
  set(tokens: Pick<AuthResponse, 'accessToken' | 'refreshToken'>) {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken)
    listeners.forEach((listener) => listener())
  },
  clear() {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    listeners.forEach((listener) => listener())
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
}
